import { TrainingSummary as Training } from "@my-helpers/common";
import { ElementType } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, Box, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import { formatDate } from "@/utils/dateHelpers";
import { Delete, Download, Refresh, RemoveCircleOutline } from "@mui/icons-material";

interface TrainingSummaryProps {
    training: Training;
    refreshTraining: (trainingId: string) => Promise<void>;
    removeTraining: (trainingId: string) => Promise<void>;
}

const TrainingSummary: ElementType<TrainingSummaryProps> = ({ 
    training, refreshTraining, removeTraining }) => {
    const navigate = useNavigate();

    const getStatusColor = (status: string) => {
        if (status === 'removed') return 'error';
        if (status === 'loaded') return 'success';
        if (status === 'removing') return 'warning';
        if (status === 'loading') return 'success';
        return 'default';
    }

    const getStatusVariant = (status: string) => {
        if (status === 'removed') return 'outlined';
        if (status === 'loaded') return 'filled';
        if (status === 'removing') return 'outlined';
        if (status === 'loading') return 'outlined';
        return 'outlined';
    }

    return (
        <Card
            onClick={() => navigate(`/trainings/${training.id || training.name}`)}
            sx={{
                height: '100%',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                }
            }}
        >
            <CardContent
                sx={{
                    padding: 2,
                    flexGrow: 1,
                    gap: 2,
                    '&:last-child': {
                        paddingBottom: '16px'
                    }
                }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                    paddingBottom: '16px'
                }}>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 400 }}
                        component="h2"
                        noWrap >
                        {training.name}
                    </Typography>
                    <Chip
                        label={training.version}
                        color="default"
                        variant={getStatusVariant(training.status)}
                        size="small"
                    />
                </Box>
                <Tooltip title={training.description || ''}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mb: 2,
                            minHeight: '2.5em', // Ensure 2 lines of height
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {training.description}
                    </Typography>
                </Tooltip>

                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap'
                }}>
                    <Chip
                        label={training.status}
                        color={getStatusColor(training.status)}
                        variant={getStatusVariant(training.status)}
                        size="small"
                    />
                    {training.tagged_by?.map((tag, index) => (
                        <Chip
                            key={index}
                            label={tag}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    ))}
                </Box>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mt: 1,
                }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                    >
                        Last updated: {formatDate(training.updated_at)}
                    </Typography>
                    {
                        training.status === 'removed' ? (
                            <Box>
                                <Tooltip title="Load model">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { 
                                            e.stopPropagation();
                                            //loadTraining(training.id);
                                        }}
                                        sx={{
                                            color: 'text.secondary',
                                            '&:hover': {
                                                color: 'primary.main'
                                            }
                                        }}
                                    >
                                        <Download fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Load model">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { 
                                            e.stopPropagation();
                                            removeTraining(training.id);
                                        }}
                                        sx={{
                                            color: 'text.secondary',
                                            '&:hover': {
                                                color: 'primary.main'
                                            }
                                        }}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ) : training.status === 'loaded' ? (
                            <Tooltip title="Remove model">
                                <IconButton
                                    size="small"
                                    onClick={(e) => { 
                                        e.stopPropagation();
                                        removeTraining(training.id);
                                    }}
                                    sx={{
                                        color: 'text.secondary',
                                        '&:hover': {
                                            color: 'error.main'
                                        }
                                    }}
                                >
                                    <RemoveCircleOutline fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : <Tooltip title="Refresh model status">
                            <IconButton
                                size="small"
                                onClick={(e) => { 
                                    e.stopPropagation();
                                    refreshTraining(training.id);
                                }}
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                        color: 'error.main'
                                    }
                                }}
                            >
                                <Refresh fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    }
                </Box>
            </CardContent>
        </Card>
    );
};

export default TrainingSummary;